import React from 'react';
import { format } from 'date-fns';
import { Quotation, QuotationStatus } from '../../types/quotation';

/**
 * Státusz-metaadatok. A régi (editing / pending / success) státuszok
 * megmaradnak az adatban, de a felületen a 4 fő státusz mellett
 * a saját színükkel jelennek meg.
 */
export const QUOTATION_STATUS_META: Record<QuotationStatus, { label: string; dot: string; chip: string; legacy?: boolean }> = {
  draft:    { label: 'Vázlat',        dot: 'bg-gray-400',    chip: 'bg-gray-100 text-gray-700 dark:bg-gray-700/60 dark:text-gray-200' },
  sent:     { label: 'Elküldve',      dot: 'bg-sky-500',     chip: 'bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300' },
  accepted: { label: 'Elfogadva',     dot: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  rejected: { label: 'Elutasítva',    dot: 'bg-rose-500',    chip: 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300' },
  editing:  { label: 'Szerkesztés alatt', dot: 'bg-gray-400', chip: 'bg-gray-100 text-gray-700 dark:bg-gray-700/60 dark:text-gray-200', legacy: true },
  pending:  { label: 'Függőben',      dot: 'bg-amber-500',   chip: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300', legacy: true },
  success:  { label: 'Elfogadva',     dot: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300', legacy: true },
};

export const PRIMARY_STATUSES: QuotationStatus[] = ['draft', 'sent', 'accepted', 'rejected'];

/** Régi státuszok besorolása a 4 fő csoportba (KPI, szűrés). */
export const normalizeStatus = (s: QuotationStatus): 'draft' | 'sent' | 'accepted' | 'rejected' => {
  if (s === 'editing') return 'draft';
  if (s === 'pending') return 'sent';
  if (s === 'success') return 'accepted';
  return s;
};

export const fmtMoney = (n: number, currency = 'HUF') =>
  new Intl.NumberFormat('hu-HU', { style: 'currency', currency, maximumFractionDigits: 0 }).format(n || 0);

export const quotationTotal = (q: Quotation): number => {
  if (typeof q.totalAmount === 'number' && q.totalAmount > 0) return q.totalAmount;
  return (q.items || []).reduce((sum, item) => {
    const line = item.lineTotalWithTax || item.totalWithTax || ((item.price || item.unitPrice || 0) * (item.quantity || 0) * (1 + (item.taxRate || 0) / 100));
    return sum + (line || 0);
  }, 0);
};

export const toDate = (v: any): Date | null => {
  if (!v) return null;
  try {
    if (typeof v === 'object' && 'toDate' in v) return v.toDate();
    if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
};

export const fmtDate = (v: any) => {
  const d = toDate(v);
  return d ? format(d, 'yyyy.MM.dd') : '–';
};

export const isExpired = (q: Quotation) => {
  const d = toDate(q.validUntil);
  if (!d) return false;
  const st = normalizeStatus(q.status);
  if (st === 'accepted' || st === 'rejected') return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d < today;
};

/* ---------- Státusz select (chip-szerű, helyben állítható) ---------- */
export const StatusSelect: React.FC<{
  value: QuotationStatus;
  onChange: (s: QuotationStatus) => void;
  size?: 'sm' | 'xs';
}> = ({ value, onChange, size = 'sm' }) => {
  const meta = QUOTATION_STATUS_META[value] || QUOTATION_STATUS_META.draft;
  return (
    <span className="relative inline-flex items-center" onClick={(e) => e.stopPropagation()}>
      <span className={`pointer-events-none absolute left-2 w-1.5 h-1.5 rounded-full ${meta.dot}`} />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as QuotationStatus)}
        className={`appearance-none cursor-pointer rounded-full border-0 pl-5 pr-6 font-medium focus:ring-2 focus:ring-primary-500/40 ${meta.chip} ${size === 'xs' ? 'h-6 text-[11px]' : 'h-7 text-xs'}`}
        style={{ backgroundImage: 'none' }}
        aria-label="Státusz"
      >
        {PRIMARY_STATUSES.map(s => (
          <option key={s} value={s}>{QUOTATION_STATUS_META[s].label}</option>
        ))}
        {meta.legacy && <option value={value}>{meta.label}</option>}
      </select>
      <svg className="pointer-events-none absolute right-2 w-3 h-3 opacity-60" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" /></svg>
    </span>
  );
};

